import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, BadgeCheck, Clock, CircleSlash, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusStyle = (status) => {
  const map = {
    active: { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200', icon: BadgeCheck, label: 'Active' },
    paused: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200', icon: Clock, label: 'Paused' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-200', icon: CircleSlash, label: 'Cancelled' },
    expired: { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-200', icon: CircleSlash, label: 'Expired' },
  };
  return map[status] || { bg: 'bg-gray-100', text: 'text-gray-700', ring: 'ring-gray-200', icon: Clock, label: status || '—' };
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtINR = (n) => {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return '—';
  return `\u20B9${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const MySubscriptions = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ active: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/subscriptions/mine`, { withCredentials: true });
        if (!alive) return;
        setItems(data.subscriptions || []);
        setCounts(data.counts || { active: 0, total: 0 });
      } catch (err) {
        if (!alive) return;
        setError(err.response?.data?.detail || 'Failed to load subscriptions');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="p-8 rounded-2xl bg-white border border-[#E0E0E0]" data-testid="my-subscriptions-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-black font-heading">My Subscriptions</h2>
        </div>
        {!loading && !error && counts.total > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="my-subscriptions-counts">
            <span className="font-bold text-green-700">{counts.active}</span> active · {counts.total} total
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your subscriptions…
        </div>
      ) : error ? (
        <div className="py-6 text-sm text-red-700" data-testid="my-subscriptions-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-sm text-muted-foreground" data-testid="my-subscriptions-empty">
          You don't have any subscriptions yet. Browse our <a className="text-primary font-medium underline-offset-2 hover:underline" href="/subscription">Ritual Subscription</a> plans.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="my-subscriptions-list">
          {items.map((s) => {
            const st = statusStyle(s.status);
            const Icon = st.icon;
            return (
              <li
                key={s.subscription_id}
                className="rounded-xl border border-[#E5E5E5] p-4 flex items-start justify-between gap-4 hover:border-primary/30 transition-colors"
                data-testid={`subscription-row-${s.subscription_id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold font-heading text-base text-foreground" data-testid={`sub-plan-name-${s.subscription_id}`}>
                      {s.plan_name || s.plan_type || 'Subscription'}
                    </div>
                    {s.plan_interval ? (
                      <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground bg-[#F4F4F4] px-2 py-0.5 rounded-full">
                        /{s.plan_interval}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      Started <span className="text-foreground font-medium" data-testid={`sub-start-${s.subscription_id}`}>{fmtDate(s.created_at)}</span>
                    </span>
                    {s.status === 'active' && s.next_renewal ? (
                      <span data-testid={`sub-next-${s.subscription_id}`}>
                        Next renewal <span className="text-foreground font-medium">{fmtDate(s.next_renewal)}</span>
                      </span>
                    ) : null}
                    {typeof s.amount === 'number' && s.amount > 0 && (
                      <span>
                        Amount <span className="text-foreground font-medium">{fmtINR(s.amount)}</span>
                      </span>
                    )}
                    {s.subscription_id && (
                      <span className="font-mono text-[10px] tracking-tight" title="Subscription ID">
                        {s.subscription_id}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 ${st.bg} ${st.text} ${st.ring}`} data-testid={`sub-status-${s.subscription_id}`}>
                  <Icon className="h-3 w-3" /> {st.label}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MySubscriptions;
